import React from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import type { TFunction } from "i18next"; // Import TFunction type

/**
 * Interface for RTK Query error object structure
 */
export interface RTKQueryError {
  status?: number;
  error?: string;
  data?:
    | {
        message?: string;
        [key: string]: any;
      }
    | string;
}

/**
 * Props for the RTKQErrorCard component
 */
export interface RTKQErrorCardProps {
  error: RTKQueryError | null;
  title?: string;
  className?: string;
  compact?: boolean;
  maxHeight?: string | number;
  showFullDetails?: boolean;
}

/**
 * Format RTK Query error data into a readable string for debugging
 *
 * @param errorObj - The error object from RTK Query
 * @param t - The translation function
 * @returns Formatted error data as a string
 */
function formatErrorData(errorObj: RTKQueryError, t: TFunction): string {
  // Add t parameter
  const errorDetails: string[] = [];

  // Add status if available
  if (errorObj.status) {
    errorDetails.push(`${t("errorCard.status")}: ${errorObj.status}`); // Use t function
  }

  // Add error message if available as string
  if (typeof errorObj.error === "string") {
    errorDetails.push(`${t("errorCard.error")}: ${errorObj.error}`); // Use t function
  }

  // Process data property (could be an object, string, or undefined)
  if (errorObj.data) {
    if (typeof errorObj.data === "object") {
      // Format object properties
      errorDetails.push(`${t("errorCard.data")}:`); // Use t function
      Object.entries(errorObj.data).forEach(([key, value]) => {
        const valueStr =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        errorDetails.push(`  ${key}: ${valueStr}`);
      });
    } else {
      // If data is a string or other primitive
      errorDetails.push(`${t("errorCard.data")}: ${String(errorObj.data)}`); // Use t function
    }
  }

  return errorDetails.join("\n");
}

/**
 * Reusable component for displaying API error data consistently
 */
const RTKQErrorCard: React.FC<RTKQErrorCardProps> = ({
  error,
  title, // Remove default value here, will be set using t function
  className = "",
  compact = false,
  maxHeight = "200px",
  showFullDetails = true,
}) => {
  const { t } = useTranslation(); // Initialize useTranslation

  if (!error) return null;

  // Use translated default title if none provided
  const cardTitle = title || t("errorCard.title");

  // Get basic error information
  const statusCode = error.status || null;

  // Format the title with status code if available
  const formattedTitle = statusCode
    ? `${cardTitle} (${statusCode})`
    : cardTitle;

  // Simple error message for non-detailed view
  const simpleErrorMessage =
    (error.data && typeof error.data === "object" && error.data.message) ||
    (typeof error.error === "string" ? error.error : t("errorCard.unknown")); // Use t function for unknown error

  // Formatted detailed error data
  const formattedErrorData = formatErrorData(error, t); // Pass t to formatErrorData

  return (
    <div className={`error-data ${compact ? "compact" : ""} ${className}`}>
      <div className="error-data__header">
        <h4 className="error-data__title">{formattedTitle}</h4>
        {!compact && (
          <span className="error-data__badge">
            {statusCode || t("errorCard.error")} {/* Use t function */}
          </span>
        )}
      </div>

      <div
        className="error-data__content"
        style={{ maxHeight: showFullDetails ? maxHeight : "auto" }}
      >
        {/* Always show simple message */}
        <div className="error-data__message">{simpleErrorMessage}</div>

        {/* Show detailed error information if requested */}
        {showFullDetails && (
          <pre className="error-data__details">{formattedErrorData}</pre>
        )}
      </div>
    </div>
  );
};

export default RTKQErrorCard;
